/**
 * CategoryPage — single-category browse experience.
 *
 * All data-fetching is encapsulated in `useCategoryData`. Presentational
 * bits live in `pages/category/CategoryRails` (switcher, hero, type
 * chips, sort bar, tabs, Section) and `pages/category/RentTheLookRail`.
 * This file is left with the page-level layout and section wiring.
 *
 * Layout (top → bottom):
 *   0. Horizontal "switch category" pill rail (in-place swap).
 *   1. Category hero banner.
 *   2. Category-aware filters (SearchFilters — reads /search/meta).
 *   3. Product-type chip rail (Pants / Shirts / …).
 *   4. Sort + New-launch filter row.
 *   5. Inner tabs: Browse / Preferences / Saved.
 *   6. Browse tab: Rent-the-look rail (if rentals-eligible) →
 *      Live streams → Trending → For-you (or set-prefs callout) →
 *      Stores → All products (filter-driven grid).
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ProductCard } from "./HomePage";
import { PreferencesTab } from "./ProfilePage";
import SearchFilters from "../components/SearchFilters";
import { Sparkles, Settings2, Radio, Flame, Store, Package, Heart, Grid3x3 } from "lucide-react";

import {
  CategorySwitcherRail, CategoryHero, ProductTypeChips,
  SortAndNewLaunchBar, InnerTab, Section,
} from "./category/CategoryRails";
import { RentTheLookRail } from "./category/RentTheLookRail";
import { useCategoryData } from "./category/useCategoryData";


export default function CategoryPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const d = useCategoryData({ id, user });

  // Sub-tab inside the category. Always starts on "browse"; "preferences"
  // and "saved" only render when the user is signed in.
  const [innerTab, setInnerTab] = useState("browse");
  useEffect(() => { setInnerTab("browse"); }, [id]);

  useEffect(() => {
    if (innerTab === "preferences") d.loadProfileAndSchema();
    if (innerTab === "saved") d.loadSavedIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerTab, user]);

  return (
    <div className="pb-24 md:pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
      <CategorySwitcherRail cats={d.cats} activeId={id}
        onSwitch={(cid) => nav(`/category/${cid}`)}/>
      <CategoryHero cat={d.cat}/>

      {/* Category-aware filters. Hide the inner category picker so we don't
          render a confusing second category selector on top of our pill rail. */}
      <div className="mb-4">
        <SearchFilters value={d.filters} onChange={d.setFilters}
          categoryId={id} hideCategoryPicker/>
      </div>

      <ProductTypeChips productTypes={d.productTypes}
        activeType={d.activeType} onChange={d.setActiveType}/>
      <SortAndNewLaunchBar sortId={d.sortId} onSortChange={d.setSortId}
        newOnly={d.newOnly} onToggleNewOnly={d.setNewOnly}/>

      {/* Sub-tabs inside the category — Browse / Preferences / Saved */}
      {user && (
        <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-[#3D2845] mb-6"
             data-testid="cat-inner-tabs">
          <InnerTab id="browse" active={innerTab} onClick={setInnerTab} icon={Grid3x3} label="Browse" color="#D4FF00"/>
          <InnerTab id="preferences" active={innerTab} onClick={setInnerTab} icon={Settings2} label="Preferences" color="#00F0FF"/>
          <InnerTab id="saved" active={innerTab} onClick={setInnerTab} icon={Heart} label="Saved" color="#FF007F"
            badge={d.savedInCategory.length || null}/>
        </div>
      )}

      {innerTab === "saved" && <SavedTabContent d={d} cat={d.cat} onBrowse={() => setInnerTab("browse")}/>}
      {innerTab === "preferences" && <PreferencesTabContent d={d} id={id}/>}
      {innerTab === "browse" && <BrowseTabContent d={d} id={id} user={user} onOpenSaved={() => setInnerTab("saved")}/>}
    </div>
  );
}


function SavedTabContent({ d, cat, onBrowse }) {
  return (
    <Section icon={Heart} accent="#FF007F"
      title={`Saved in ${cat?.name || "this category"}`}
      testid="sec-saved-in-category">
      {!d.savedLoaded ? (
        <div className="text-sm text-[#A1A1AA] text-center py-10">Loading…</div>
      ) : d.savedInCategory.length === 0 ? (
        <div className="card p-8 text-center">
          <Heart size={28} className="text-[#FF007F] mx-auto mb-3"/>
          <div className="font-bold text-base">No saved items yet</div>
          <div className="text-sm text-[#A1A1AA] mt-1">
            Tap the heart on any product in this category to save it for later.
          </div>
          <button onClick={onBrowse}
            data-testid="saved-empty-browse-btn"
            className="btn-primary mt-4 text-xs">Browse {cat?.name}</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {d.savedInCategory.map((p, i) => <ProductCard key={p.product_id} p={p} index={i}/>)}
        </div>
      )}
    </Section>
  );
}


function PreferencesTabContent({ d, id }) {
  return (
    <Section icon={Settings2} accent="#00F0FF"
      title="Preferences" testid="sec-preferences-inline">
      {d.prefMsg && <div className="mb-3 text-xs text-[#FF007F]">{d.prefMsg}</div>}
      {!d.profile || !d.prefSchema ? (
        <div className="text-sm text-[#A1A1AA] text-center py-10">Loading…</div>
      ) : !d.prefSchema[id] ? (
        <div className="card p-8 text-center">
          <Sparkles size={28} className="text-[#D4FF00] mx-auto mb-3"/>
          <div className="font-bold text-base">No preferences for this category yet</div>
          <div className="text-sm text-[#A1A1AA] mt-1">
            This category doesn't expose tunable preferences. You can still manage prefs for other categories in your profile.
          </div>
          <Link to="/profile?tab=preferences"
            className="btn-secondary mt-4 text-xs inline-flex"
            data-testid="sec-prefs-goto-profile">
            Open full preferences
          </Link>
        </div>
      ) : (
        <PreferencesTab profile={d.profile} schema={d.prefSchema}
          categoryFilter={id}
          onUpdated={async () => {
            d.setPrefMsg("Preferences saved.");
            const { data } = await api.get("/buyer/profile");
            d.setProfile(data);
            setTimeout(() => d.setPrefMsg(null), 1800);
          }}
          onError={(m) => { d.setPrefMsg(m); setTimeout(() => d.setPrefMsg(null), 3000); }}/>
      )}
    </Section>
  );
}


function BrowseTabContent({ d, id, user, onOpenSaved }) {
  return (
    <>
      {/* Saved-in-category nudge on the Browse tab when the buyer has any */}
      {user && d.savedInCategory.length > 0 && (
        <div className="card p-3 mb-6 flex items-center gap-3" data-testid="saved-nudge">
          <span className="w-9 h-9 rounded-xl bg-[#FF007F]/15 flex items-center justify-center">
            <Heart size={16} className="text-[#FF007F]"/>
          </span>
          <div className="flex-1">
            <div className="text-sm font-bold">
              You have {d.savedInCategory.length} saved item{d.savedInCategory.length === 1 ? "" : "s"} in {d.cat?.name || "this category"}
            </div>
          </div>
          <button onClick={onOpenSaved}
            data-testid="saved-nudge-btn"
            className="text-xs font-bold text-[#FF007F] hover:underline">View →</button>
        </div>
      )}

      {d.catIsRentable && (
        <RentTheLookRail rentals={d.rentals}
          categoryId={id} categoryName={d.cat?.name}/>
      )}

      {/* 1 — Live streams */}
      {d.streams.length > 0 && (
        <Section icon={Radio} accent="#FF007F" title="Live now" testid="sec-live">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {d.streams.map(s => (
              <Link key={s.stream_id} to={`/live/${s.stream_id}`}
                data-testid={`cat-live-${s.stream_id}`} className="card block relative">
                <div className="aspect-video">
                  <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover"/>
                </div>
                <div className="absolute top-2 left-2 bg-[#FF007F] text-white text-[10px] font-bold uppercase px-1.5 py-0.5 rounded">LIVE</div>
                <div className="p-3 text-sm font-semibold line-clamp-1">{s.title}</div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* 2 — Trending products in category */}
      {d.trending.length > 0 && (
        <Section icon={Flame} accent="#FF007F" title="Trending" testid="sec-trending">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {d.trending.slice(0, 8).map((p, i) => <ProductCard key={p.product_id} p={p} index={i}/>)}
          </div>
        </Section>
      )}

      {/* 3 — For-you rail or set-prefs callout */}
      {user && <ForYouOrSetPrefsCallout d={d}/>}

      {/* 4 — Stores */}
      {d.stores.length > 0 && (
        <Section icon={Store} accent="#D4FF00" title="Stores" testid="sec-stores">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {d.stores.map(s => (
              <Link key={s.store_id} to={`/store/${s.store_id}`}
                data-testid={`cat-store-${s.store_id}`} className="card block hover:border-[#D4FF00] transition">
                <div className="h-24"><img src={s.banner_url} alt="" className="w-full h-full object-cover"/></div>
                <div className="p-3">
                  <div className="font-bold text-sm truncate">{s.name}</div>
                  <div className="text-xs text-[#A1A1AA]">{s.followers || 0} followers</div>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Full filtered product grid */}
      <Section icon={Package} accent="#D4FF00" title="All products" testid="sec-all-products">
        {d.products.length === 0 ? (
          <div className="text-center text-[#A1A1AA] py-16">No products match these filters. Try clearing some.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {d.products.map((p, i) => <ProductCard key={p.product_id} p={p} index={i}/>)}
          </div>
        )}
      </Section>
    </>
  );
}


function ForYouOrSetPrefsCallout({ d }) {
  if (d.isPersonalized && d.forYou.length > 0) {
    return (
      <Section icon={Sparkles} accent="#D4FF00" title="For you · AI picks" testid="sec-for-you"
        cta={
          <Link to="/profile?tab=preferences"
            data-testid="for-you-edit-prefs"
            className="text-xs font-bold text-[#D4FF00] hover:underline flex items-center gap-1">
            <Settings2 size={12}/> Edit preferences
          </Link>
        }>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {d.forYou.slice(0, 8).map((p, i) => <ProductCard key={p.product_id} p={p} index={i}/>)}
        </div>
      </Section>
    );
  }
  return (
    <div className="mb-10 card p-5 sm:p-6 grain relative overflow-hidden" data-testid="set-prefs-callout">
      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-[#D4FF00]"/>
            <div className="text-xs font-bold uppercase tracking-widest text-[#D4FF00]">Unlock "For you"</div>
          </div>
          <div className="font-display font-black text-lg sm:text-xl">
            Set shopping preferences to get a personalised rail on every category.
          </div>
          <p className="text-xs text-[#A1A1AA] mt-1">Style, size, gender, budget, favourite brands — tune it once, get smarter picks everywhere.</p>
        </div>
        <Link to="/profile?tab=preferences"
          data-testid="set-prefs-btn"
          className="btn-primary shrink-0 self-start sm:self-auto">
          <Settings2 size={14} className="inline mr-1"/> Set preferences
        </Link>
      </div>
    </div>
  );
}
